import { useCallback, useEffect, useRef, useState } from 'react';
import { prepareImageForUpload } from '@/lib/images/prepare-image-upload';

/**
 * Owns the "pick an image, show a preview, upload it on save" state shared by
 * the pricebook create and edit forms.
 *
 * Two things this centralises that both callers previously got wrong:
 *
 * 1. The selected file is downscaled before it is ever put in the DOM (see
 *    `prepareImageForUpload`) — a full-resolution phone photo decoded into an
 *    88px preview is hundreds of megabytes of raster memory.
 * 2. Object URLs are revoked. Both forms called `URL.createObjectURL` on every
 *    selection and never released it, so re-picking an image repeatedly leaked
 *    a decoded copy each time — the same pressure that makes (1) bite.
 *
 * `preview` may also be a plain remote URL (the edit form seeds it from the
 * saved item), so revocation is guarded on the `blob:` scheme.
 */
export function useImagePreview() {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [preview, setPreviewState] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Distinguishes "no local file yet" from an explicit removal of a saved
  // remote image. Edit forms need the latter to send `imageUrl: null` and to
  // avoid immediately seeding the old preview again.
  const [isImageCleared, setIsImageCleared] = useState(false);
  // Only blob: URLs we minted are ours to revoke.
  const objectUrlRef = useRef<string | null>(null);
  // Bumped by every action that changes what the user has chosen, so an
  // in-flight `selectImage` can tell whether its result is still wanted.
  const selectionRef = useRef(0);
  const mountedRef = useRef(true);

  const releaseObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      releaseObjectUrl();
    };
  }, [releaseObjectUrl]);

  const selectImage = useCallback(
    async (file: File) => {
      const selection = ++selectionRef.current;
      // A replacement is the user's current intent from the moment they pick
      // it, not only after an expensive downscale finishes. Clearing this
      // synchronously prevents a quick Save after Remove -> Replace from
      // persisting the stale removal intent.
      setIsImageCleared(false);
      setIsProcessing(true);
      const prepared = await prepareImageForUpload(file);
      // Downscaling is async and its cost scales with the image, so picking a
      // 60MP photo and then swapping to a small one lets the *first* call
      // resolve last. Applying it would silently restore the replaced image
      // and upload it on save. Only the newest selection may land — and
      // nothing may land after unmount, which would leak the object URL past
      // the cleanup that was supposed to release it.
      if (selection !== selectionRef.current || !mountedRef.current) return;
      releaseObjectUrl();
      const url = URL.createObjectURL(prepared);
      objectUrlRef.current = url;
      setPendingFile(prepared);
      setPreviewState(url);
      setIsProcessing(false);
    },
    [releaseObjectUrl],
  );

  const clearImage = useCallback(() => {
    // Also discards anything still downscaling, so removing the image while a
    // large one is being processed cannot resurrect it a moment later.
    selectionRef.current++;
    releaseObjectUrl();
    setIsProcessing(false);
    setPendingFile(null);
    setPreviewState(null);
    setIsImageCleared(true);
  }, [releaseObjectUrl]);

  /**
   * Seed the preview from an already-saved image URL (edit form).
   *
   * Deliberately does *not* bump `selectionRef`: the edit form calls this from
   * an effect, and while a pick is still downscaling `pendingFile` is null, so
   * that effect can fire mid-selection. Discarding the in-flight result here
   * would throw away the image the user just chose in favour of the saved one.
   */
  const setRemotePreview = useCallback(
    (url: string | null) => {
      releaseObjectUrl();
      setPreviewState(url);
      setIsImageCleared(false);
    },
    [releaseObjectUrl],
  );

  return {
    pendingFile,
    preview,
    isImageCleared,
    isProcessing,
    selectImage,
    clearImage,
    setRemotePreview,
  };
}
